import { ComponentProps, createContext, useContext } from "react";
import { Icon, IconNames } from "../Common/Icons/LucideIcons";
import { Body } from "../Common/Typography";
import {
  CommonContentIconWrapper,
  CommonContentTextBlock,
  CommonContentWrapper,
  GroupWrapper,
  StyledSelectionCard,
} from "./__styles__/SelectionCard";

export type SelectionCardOption = {
  /**
   * An ID value is required for the React `key` prop. Consider using
   * an actual unique identifier or a combination of the name of the
   * field and its value
   */
  id: string;
  value: string;
  title: string;
  description?: string;
  iconName?: IconNames;
  disabled?: boolean;
};

type SelectionCardItemProps<T> = Omit<
  ComponentProps<typeof StyledSelectionCard>,
  "value" | "onClick" | "key"
> & {
  key: string | number;
  borderStyle: "ghost" | "outline";
  size: "default" | "compact";
  value: T;
};
export const SelectionCardItem = <T,>({
  value,
  children,
  ...props
}: SelectionCardItemProps<T>) => {
  const { handleSelection, currentValue } = useContext(
    SelectionCardContext,
  ) as SelectionCardContextType<T>;
  if (!handleSelection) {
    throw new Error(
      "SelectionCardItem must be used within a SelectionCardGroup",
    );
  }
  const isSelected = currentValue === value;
  return (
    <StyledSelectionCard
      type="button"
      role="radio"
      onClick={() => handleSelection(value)}
      selected={isSelected}
      aria-checked={isSelected}
      {...props}
    >
      {children}
    </StyledSelectionCard>
  );
};

type SelectionCardContextType<T> = {
  handleSelection: ((value: T) => void) | null;
  currentValue?: T;
};

export const SelectionCardContext = createContext<
  SelectionCardContextType<any>
>({
  handleSelection: null,
});

type SelectionCardGroupProps<T> = ComponentProps<typeof GroupWrapper> & {
  direction: "vertical" | "horizontal";
  spacing: "default" | "compact";
  handleSelection: (value: T) => void;
  currentValue?: T;
};

const SelectionCardGroup = <T,>({
  children,
  handleSelection,
  currentValue,
  ...props
}: SelectionCardGroupProps<T>) => (
  <SelectionCardContext.Provider value={{ handleSelection, currentValue }}>
    <GroupWrapper role="radiogroup" {...props}>
      {children}
    </GroupWrapper>
  </SelectionCardContext.Provider>
);

const SelectionCardCommonContent = ({
  iconName,
  title,
  description,
  disabled = false,
}: {
  iconName?: IconNames;
  title?: Maybe<string>;
  description?: Maybe<string>;
  disabled?: boolean;
}) => (
  <CommonContentWrapper>
    {iconName && (
      <CommonContentIconWrapper>
        <Icon
          iconName={iconName}
          color={disabled ? "contentDisabled" : "contentSecondary"}
          size={16}
        />
      </CommonContentIconWrapper>
    )}
    {(title || description) && (
      <CommonContentTextBlock>
        {title ?
          <Body
            size="default"
            type="emphasis"
            color={disabled ? "contentDisabled" : "contentPrimary"}
          >
            {title}
          </Body>
        : null}
        {description ?
          <Body
            color={disabled ? "contentDisabled" : "contentSecondary"}
            size="default"
            type="regular"
          >
            {description}
          </Body>
        : null}
      </CommonContentTextBlock>
    )}
  </CommonContentWrapper>
);

export const SelectionCard = {
  Group: SelectionCardGroup,
  Item: SelectionCardItem,
  CommonContent: SelectionCardCommonContent,
};
